import { useState } from "react";
import { X } from "lucide-react";
import { EDUCATION } from "../data/content";
import { Reveal, SectionHeader } from "./ui-bits";

const PINS = [
  { x: 150, y: 348, label: "12TH", below: true },
  { x: 345, y: 228, label: "B.COM (HONS.)", below: false },
  { x: 610, y: 92, label: "MBA", below: false },
];
const TRAIL =
  "M60 385 C 110 372 150 355 175 335 C 195 318 185 305 210 292 C 240 277 275 285 300 268 C 325 252 315 238 345 228 C 380 216 420 222 452 204 C 482 187 470 165 500 150 C 532 134 566 142 595 118 C 618 99 648 88 672 68";
const ZOOM = 2.1;

/* ---- topo contour lines generated from a seeded terrain field (marching squares) ---- */
function buildContours({ width, height, step, levels, field }) {
  const nx = Math.ceil(width / step) + 1;
  const ny = Math.ceil(height / step) + 1;
  const vals = new Float32Array(nx * ny);
  for (let j = 0; j < ny; j++)
    for (let i = 0; i < nx; i++)
      vals[j * nx + i] = field(i * step, j * step);

  const kk = (p) => `${Math.round(p[0] * 10)}:${Math.round(p[1] * 10)}`;
  const out = [];

  for (let li = 0; li < levels.length; li++) {
    const level = levels[li];
    const segs = [];
    for (let j = 0; j < ny - 1; j++) {
      for (let i = 0; i < nx - 1; i++) {
        const v0 = vals[j * nx + i], v1 = vals[j * nx + i + 1];
        const v3 = vals[(j + 1) * nx + i], v2 = vals[(j + 1) * nx + i + 1];
        const c = (v0 > level ? 8 : 0) | (v1 > level ? 4 : 0) | (v2 > level ? 2 : 0) | (v3 > level ? 1 : 0);
        if (c === 0 || c === 15) continue;
        const x = i * step, y = j * step;
        const ip = (a, b) => (level - a) / (b - a);
        const top = [x + step * ip(v0, v1), y];
        const right = [x + step, y + step * ip(v1, v2)];
        const bottom = [x + step * ip(v3, v2), y + step];
        const left = [x, y + step * ip(v0, v3)];
        const add = (a, b) => segs.push([a, b]);
        if (c === 1 || c === 14) add(left, bottom);
        else if (c === 2 || c === 13) add(bottom, right);
        else if (c === 3 || c === 12) add(left, right);
        else if (c === 4 || c === 11) add(top, right);
        else if (c === 6 || c === 9) add(top, bottom);
        else if (c === 7 || c === 8) add(left, top);
        else { add(left, top); add(bottom, right); }
      }
    }

    const byKey = new Map();
    segs.forEach((s, si) => {
      for (const p of s) {
        const k = kk(p);
        if (!byKey.has(k)) byKey.set(k, []);
        byKey.get(k).push(si);
      }
    });
    const used = new Uint8Array(segs.length);
    let d = "";
    for (let si = 0; si < segs.length; si++) {
      if (used[si]) continue;
      used[si] = 1;
      const line = [segs[si][0], segs[si][1]];
      while (line.length < 5000) {
        const k = kk(line[line.length - 1]);
        const cands = byKey.get(k) || [];
        let nj = -1;
        for (const cj of cands) if (!used[cj]) { nj = cj; break; }
        if (nj < 0) break;
        used[nj] = 1;
        const [p, q] = segs[nj];
        line.push(kk(p) === k ? q : p);
      }
      if (line.length < 3) continue;
      const r = (n) => Math.round(n * 10) / 10;
      const closed = kk(line[0]) === kk(line[line.length - 1]);
      d += `M${r(line[0][0])} ${r(line[0][1])}`;
      for (let i2 = 1; i2 < line.length - 1; i2++) {
        const mx = (line[i2][0] + line[i2 + 1][0]) / 2;
        const my = (line[i2][1] + line[i2 + 1][1]) / 2;
        d += `Q${r(line[i2][0])} ${r(line[i2][1])} ${r(mx)} ${r(my)}`;
      }
      const e = line[line.length - 1];
      d += `L${r(e[0])} ${r(e[1])}${closed ? "Z" : ""}`;
    }
    if (d) out.push({ d, index: li % 5 === 0 });
  }
  return out;
}

let _seed = 11;
const rnd = () => { _seed = (_seed * 16807) % 2147483647; return _seed / 2147483647; };
/* the MBA summit (672, 75) is the highest point — the flag sits on the peak ring;
   smaller hills sit west, southwest (trailhead side) and southeast */
const HILLS = [
  [672, 75, 1.0, 95], [200, 115, 0.62, 85], [150, 355, 0.5, 70], [610, 345, 0.45, 68], [400, 250, 0.22, 110],
];
const RIPPLES = Array.from({ length: 14 }, () => [rnd() * 800, rnd() * 420, (rnd() * 2 - 1) * 0.055, 30 + rnd() * 45]);
const terrain = (x, y) => {
  let h = 0;
  for (const [px, py, a, s] of HILLS) { const dx = x - px, dy = y - py; h += a * Math.exp(-(dx * dx + dy * dy) / (2 * s * s)); }
  for (const [px, py, a, s] of RIPPLES) { const dx = x - px, dy = y - py; h += a * Math.exp(-(dx * dx + dy * dy) / (2 * s * s)); }
  return h;
};
const CONTOURS = buildContours({
  width: 800, height: 420, step: 8,
  levels: Array.from({ length: 15 }, (_, i) => 0.045 + i * 0.064),
  field: terrain,
});


export default function Education() {
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(null);

  const zoomed = open !== null;
  const tx = zoomed ? 400 - ZOOM * PINS[open].x : 0;
  const ty = zoomed ? 210 - ZOOM * PINS[open].y : 0;
  const entry = zoomed ? EDUCATION[open] : null;

  return (
    <section id="education" data-testid="education-section" className="relative scroll-mt-24 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeader
          index="03"
          station="Education"
          id="SHEET 03 // EDUCATION ROUTE"
          title={<>Route Map<br /><span className="text-ember">Background</span></>}
          sub="A proper trekking route — switchbacks and all — plotted from trailhead to the MBA summit. Hover a camp to locate it on the sheet; click a pin to zoom the map and open its camp log."
        />

        <div className="grid items-stretch gap-6 lg:grid-cols-[1.35fr_1fr]">
          {/* map sheet */}
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-cream/10 bg-[#F2ECDD]" data-testid="education-map">
              {/* title block */}
              <div className="absolute left-5 top-5 z-10 border-2 border-[#111611]/70 bg-[#F2ECDD] px-3 py-2">
                <p className="font-mono text-[9px] font-bold tracking-[0.25em] text-[#111611]/70">MAP SHEET 03</p>
                <p className="font-display text-sm font-black uppercase tracking-wide text-[#111611]">Education Route</p>
              </div>
              {/* compass rose */}
              <div className="absolute bottom-12 right-5 z-10 flex h-14 w-14 items-center justify-center rounded-full border-2 border-[#111611]/50 bg-[#F2ECDD]" aria-hidden="true">
                <svg viewBox="0 0 40 40" className="h-9 w-9">
                  <polygon points="20,6 24,22 20,18 16,22" fill="#E85A24" />
                  <text x="20" y="34" textAnchor="middle" fill="#111611" style={{ font: "700 9px 'JetBrains Mono', monospace" }}>N</text>
                </svg>
              </div>

              <svg viewBox="0 0 800 420" className="block w-full">
                {/* zoomable map content */}
                <g
                  style={{
                    transform: zoomed ? `translate(${tx}px, ${ty}px) scale(${ZOOM})` : "translate(0px, 0px) scale(1)",
                    transformOrigin: "0 0",
                    transition: "transform 0.9s cubic-bezier(0.22, 1, 0.36, 1)",
                  }}
                >
                  {/* survey grid */}
                  {[100, 200, 300, 400, 500, 600, 700].map((x) => (
                    <line key={`x${x}`} x1={x} y1="0" x2={x} y2="420" stroke="#111611" strokeOpacity="0.06" />
                  ))}
                  {[70, 140, 210, 280, 350].map((y) => (
                    <line key={`y${y}`} x1="0" y1={y} x2="800" y2={y} stroke="#111611" strokeOpacity="0.06" />
                  ))}

                  {/* topo contour lines — generated terrain contours, guidebook style */}
                  <g fill="none" stroke="#111611" data-testid="education-contours">
                    {CONTOURS.map((c, i) => (
                      <path key={i} d={c.d} strokeWidth={c.index ? 1.1 : 0.7} strokeOpacity={c.index ? 0.45 : 0.3} />
                    ))}
                  </g>

                  {/* trekking trail with switchbacks */}
                  <path d={TRAIL} fill="none" stroke="#111611" strokeWidth="2.5" strokeDasharray="7 7" className="route-dash" />

                  {/* summit flag at the MBA peak */}
                  <g data-testid="education-summit-flag" className={active === 2 || open === 2 ? "glow-pulse" : ""}>
                    <line x1="672" y1="66" x2="672" y2="30" stroke="#111611" strokeWidth="3" strokeLinecap="round" />
                    <polygon points="674,30 702,38 674,46" fill="#E85A24" />
                  </g>

                  {/* scale bar */}
                  <g stroke="#111611" strokeWidth="2">
                    <line x1="40" y1="402" x2="140" y2="402" />
                    <line x1="40" y1="396" x2="40" y2="408" />
                    <line x1="90" y1="396" x2="90" y2="408" />
                    <line x1="140" y1="396" x2="140" y2="408" />
                  </g>
                  <text x="152" y="406" fill="#111611" fillOpacity="0.7" style={{ font: "500 9px 'JetBrains Mono', monospace" }}>0 — 5 KM</text>

                  {/* click-away layer to close the zoom */}
                  <rect
                    x="-400" y="-400" width="1600" height="1400" fill="transparent"
                    className={zoomed ? "cursor-pointer" : ""}
                    onClick={() => zoomed && setOpen(null)}
                    data-testid="education-map-dismiss"
                  />

                  {/* camp pins */}
                  {PINS.map((p, i) => {
                    const on = active === i || open === i;
                    return (
                      <g
                        key={p.label}
                        data-testid={`education-pin-${i + 1}`}
                        className="cursor-pointer"
                        onClick={() => setOpen(i)}
                      >
                        <circle cx={p.x} cy={p.y} r={34} fill="transparent" />
                        <circle cx={p.x} cy={p.y} r={on ? 22 : 15} fill={on ? "#E85A24" : "#F2ECDD"} stroke="#111611" strokeWidth="2.5" style={{ transition: "all .3s cubic-bezier(.22,1,.36,1)" }} />
                        <text x={p.x} y={p.y + 4.5} textAnchor="middle" fill={on ? "#F2ECDD" : "#111611"} style={{ font: "700 13px 'JetBrains Mono', monospace", transition: "fill .3s", pointerEvents: "none" }}>
                          {i + 1}
                        </text>
                        <text
                          x={p.x}
                          y={p.below ? p.y + 42 : p.y - 26}
                          textAnchor={i === 0 ? "start" : "middle"}
                          fill="#111611"
                          style={{ font: "700 11px 'JetBrains Mono', monospace", letterSpacing: "1.5px", pointerEvents: "none" }}
                        >
                          {p.label}
                        </text>
                      </g>
                    );
                  })}
                </g>
              </svg>

              {/* camp log card */}
              {zoomed && (
                <div className="absolute inset-x-4 bottom-14 z-20 sm:inset-x-auto sm:bottom-4 sm:left-4 sm:w-[330px]" data-testid="education-camp-card">
                  <div className="border-2 border-[#111611]/70 bg-[#F2ECDD] p-5 shadow-[10px_10px_0_rgba(17,22,17,0.18)]">
                    <div className="flex items-start justify-between gap-3">
                      <p className="pt-0.5 font-mono text-[9px] font-bold tracking-[0.3em] text-[#111611]/60">
                        {open === 2 ? "SUMMIT CAMP" : `CAMP 0${open + 1}`} — CAMP LOG
                      </p>
                      <button
                        onClick={() => setOpen(null)}
                        data-testid="education-camp-close"
                        className="-mr-1 -mt-1 p-1 text-[#111611]/60 transition-colors hover:text-[#E85A24]"
                        aria-label="Close camp log"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="mt-2 font-display text-2xl font-black uppercase leading-none text-[#111611]" data-testid="education-camp-degree">
                      {entry.degree}
                    </p>
                    <p className="mt-2 font-mono text-[10px] font-bold tracking-[0.22em] text-[#111611]/70">
                      {entry.year} · {entry.place.toUpperCase()}
                    </p>
                    <p className="mt-3 border-t border-dashed border-[#111611]/25 pt-3 font-mono text-[10px] leading-relaxed text-[#111611]/60">
                      {entry.note}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between border-t-2 border-dashed border-[#111611]/20 px-5 py-3 font-mono text-[9px] tracking-[0.25em] text-[#111611]/60">
                <span>SHEET 03 — EDUCATION ROUTE</span>
                <span>NOT TO SCALE — MEMORIES TO SCALE</span>
              </div>
            </div>
          </Reveal>

          {/* legend */}
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col border border-cream/10 bg-ink/50 p-7 md:p-8" data-testid="education-legend">
              <p className="border-b border-cream/10 pb-4 font-mono text-[10px] font-bold tracking-[0.3em] text-ember">ROUTE LEGEND</p>
              <div className="mt-5 flex-1 space-y-3">
                {EDUCATION.map((e, i) => {
                  const on = active === i;
                  return (
                    <button
                      key={e.year}
                      onMouseEnter={() => setActive(i)}
                      onMouseLeave={() => setActive(null)}
                      onClick={() => setOpen(open === i ? null : i)}
                      data-testid={`education-card-${e.year}`}
                      className={`w-full border p-5 text-left transition-all duration-300 ${on || open === i ? "border-ember bg-ember/10" : "border-cream/12 bg-panel/60 hover:border-cream/30"}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <span className="flex items-center gap-3">
                          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 font-mono text-[11px] font-bold transition-colors ${on || open === i ? "border-ember bg-ember text-night" : "border-cream/30 text-cream/60"}`}>
                            {i + 1}
                          </span>
                          <span className="font-display text-xl font-black uppercase text-white">{e.degree}</span>
                        </span>
                        <span className="font-display text-2xl font-black text-cream/15">{e.year}</span>
                      </div>
                      <p className="mt-2.5 pl-11 text-left font-mono text-[9px] tracking-[0.25em] text-cream/50">
                        {i === 2 ? "SUMMIT CAMP" : `CAMP 0${i + 1}`} · {e.place.toUpperCase()}
                      </p>
                    </button>
                  );
                })}
              </div>
              <p className="mt-6 border-t border-dashed border-cream/10 pt-4 font-mono text-[9px] leading-relaxed tracking-[0.2em] text-cream/40">
                THREE CAMPS PLOTTED. CLICK A PIN TO READ THE CAMP LOG.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
